import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  ChevronDown,
  CreditCard,
  FolderOpen,
  LayoutDashboard,
  LogOut,
  User,
} from 'lucide-react';
import { useAuth } from '../context/AuthProvider';

const Wrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: .5rem;
  font-size: .9rem;
`;

const Trigger = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  border: 1px solid rgba(224, 161, 94, 0.58);
  background: transparent;
  color: inherit;
  border-radius: 10px;
  padding: 0.5rem 0.75rem;
  font-weight: 700;
  cursor: pointer;
`;

const TriggerLabel = styled.span`
  line-height: 1;
`;

const TriggerCaret = styled(ChevronDown)<{ $open: boolean }>`
  transition: transform 0.2s ease;
  transform: rotate(${({ $open }) => ($open ? '180deg' : '0deg')});
`;

const Dropdown = styled.ul<{ open: boolean }>`
  position: absolute;
  right: 0;
  top: calc(100% + .5rem);
  background: #0f1322;
  color: #e8eaed;
  list-style: none;
  margin: 0;
  padding: .45rem 0;
  border-radius: 10px;
  border: 1px solid rgba(224, 161, 94, 0.35);
  box-shadow: 0 8px 20px rgba(0,0,0,.25);
  min-width: 170px;
  opacity: ${({ open }) => (open ? 1 : 0)};
  transform: translateY(${({ open }) => (open ? '0' : '-6px')});
  pointer-events: ${({ open }) => (open ? 'auto' : 'none')};
  transition: all .2s;

  li {
    display: flex;
    align-items: center;
    gap: .5rem;
    padding: .55rem .85rem;
    cursor: pointer;
    font-weight: 600;
    border-left: 2px solid transparent;

    &:hover {
      background: rgba(224, 161, 94, 0.14);
      color: #fff;
    }

    &.active {
      background: rgba(224, 161, 94, 0.18);
      border-left-color: rgba(224, 161, 94, 0.92);
      color: #fff;
    }
  }
`;

export default function UserMenu() {
  const {
    user,
    role,
    logout,
    isPrimaryAccountHolder,
    hasOrganizationMembership,
  } = useAuth();
  const [open, setOpen]  = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const rootRef = useRef<HTMLDivElement | null>(null);

  const path = location.pathname;
  const isDashboardPath = path === '/dashboard' || path.startsWith('/dashboard/home');
  const isProfilePath = path.startsWith('/dashboard/profile') || path.startsWith('/dashboard/users/profile/');
  const isProjectsPath = path.startsWith('/dashboard/projects');
  const isBillingPath = path.startsWith('/dashboard/billing');
  const hasClientWorkspaceAccess = isPrimaryAccountHolder || hasOrganizationMembership;
  const dashboardTarget =
    role === 'client'
      ? (hasClientWorkspaceAccess ? '/dashboard/home' : '/dashboard/profile')
      : '/dashboard';

  useEffect(() => {
    if (!open) return;
    const onDocMouseDown = (event: MouseEvent) => {
      if (!rootRef.current) return;
      if (rootRef.current.contains(event.target as Node)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [open]);

  if (!user) return null;

  return (
    <Wrapper ref={rootRef}>
      <Trigger type="button" onClick={() => setOpen(o => !o)} aria-label="Account menu">
        <TriggerLabel>Account</TriggerLabel>
        <TriggerCaret size={16} $open={open} />
      </Trigger>

      <Dropdown open={open}>
        <li
          className={isDashboardPath ? 'active' : ''}
          onClick={() => {
            setOpen(false);
            navigate(dashboardTarget);
          }}
        >
          <LayoutDashboard size={16} />
          Dashboard
        </li>
        <li
          className={isProfilePath ? 'active' : ''}
          onClick={() => {
            setOpen(false);
            navigate('/dashboard/profile');
          }}
        >
          <User size={16}/>
          Profile
        </li>
        {role === 'client' && hasClientWorkspaceAccess ? (
          <>
            <li
              className={isProjectsPath ? 'active' : ''}
              onClick={() => {
                setOpen(false);
                navigate('/dashboard/projects');
              }}
            >
              <FolderOpen size={16} />
              Projects
            </li>
            <li
              className={isBillingPath ? 'active' : ''}
              onClick={() => {
                setOpen(false);
                navigate('/dashboard/billing');
              }}
            >
              <CreditCard size={16} />
              Billing
            </li>
          </>
        ) : null}
        <li
          onClick={() => {
            setOpen(false);
            void logout();
          }}
        >
          <LogOut size={16}/>
          Logout
        </li>
      </Dropdown>
    </Wrapper>
  );
}
